import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Divider,
  Header,
  Loader,
  Pagination,
  Table
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature.jsx";
import ScheduleHeader from "../../components/schedules/ScheduleHeader.jsx";
import ScheduleRow from "../../components/schedules/ScheduleRow.jsx";
import RedirectWithReset from "../../components/utils/RedirectWithReset.jsx";
import ScheduleFilters from "../../components/schedules/ScheduleFilters.jsx";

import withConfirmationModal from "../../components/modals/withConfirmationModal.js";

import { SCHEDULES } from "../../state/actions/actionConstants.js";
import { OPERATIONS } from "../../constants/operations.js";
import { AT_TYPES } from "../../constants/entity.js";

import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils.js";
import { getSchedules } from "../../state/reducers/schedules/scheduleActions.js";
import { operationAction } from "../../state/reducers/operations/operationActions.js";

import scheduleSelector from "../../state/reducers/schedules/scheduleSelector.js";
import { selectOperationAvailability } from "../../state/reducers/endpoints/endpointSelector.js";

import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class Schedules extends Component {
  static propTypes = {
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    getSchedules: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired,
    schedules: PropTypes.object,
    listScheduleAvailability: PropTypes.object.isRequired,
    copyKey: PropTypes.string.isRequired,
    copy: PropTypes.func.isRequired,

    openModal: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "schedules"
  };

  state = {
    filters: null,
    activeSchedule: null,
    redirect: null
  };

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    const {
      location,
      getSchedules,
      listScheduleAvailability: { available }
    } = this.props;

    if (available) {
      getSchedules(location.search, true);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      location,
      getSchedules,
      listScheduleAvailability: { available }
    } = this.props;

    // do search params change
    if (location.search !== prevProps.location.search && available) {
      getSchedules(location.search, true);
    }
  }

  // -- HANDLERS -- //

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  // -- MODAL -- //

  handleDelete = activeSchedule => {
    const { dataMap } = this.props;

    this.setState({ activeSchedule }, () => {
      this.props.openModal(
        this.doDelete,
        "schedule.modals.delete",
        undefined,
        dataMap[activeSchedule]
      );
    });
  };

  doDelete = () => {
    const { operationAction, dataMap } = this.props;
    const { activeSchedule } = this.state;
    operationAction(dataMap[activeSchedule], OPERATIONS.DELETE, undefined, {
      success: getSchedules(),
      autoComplete: false
    });
  };

  // -- RENDER -- //

  renderHeaderSet() {
    const { view, filters } = this.state;
    const { copyKey, copy } = this.props;
    return (
      <Fragment>
        <ScheduleHeader />
        <ScheduleFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters, view }}
        />
        <Header as="h3">{copy(`${copyKey}.title`)}</Header>
      </Fragment>
    );
  }

  renderScheduleRows() {
    const { dataMap, dataIndex } = this.props;

    if (!dataMap) return null;

    return dataIndex.map(index => {
      const schedule = dataMap[index];

      return (
        <ScheduleRow
          key={schedule.id}
          schedule={schedule}
          onDelete={this.handleDelete}
        />
      );
    });
  }

  render() {
    const { redirect } = this.state;
    const {
      page,
      totalPages,
      dataIndex,
      dataMap,
      location,
      isFetching,
      listScheduleAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (!permitted) {
      return <UnauthorisedFeature title={copy(`${copyKey}.title`)} />;
    }

    // early return if project isn't ready to render
    if (isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    if (!enabled || !dataMap || dataIndex.length < 1) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <p>{copy(`${copyKey}.no_schedules`)}</p>
        </Container>
      );
    }

    return (
      <Container>
        {this.renderHeaderSet()}

        <Table celled basic striped selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{copy(`${copyKey}.name`)}</Table.HeaderCell>
              <Table.HeaderCell>{copy(`${copyKey}.timezone`)}</Table.HeaderCell>
              <Table.HeaderCell>{copy(`${copyKey}.from`)}</Table.HeaderCell>
              <Table.HeaderCell>{copy(`${copyKey}.to`)}</Table.HeaderCell>
              <Table.HeaderCell>{copy(`${copyKey}.days`)}</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>{this.renderScheduleRows()}</Table.Body>
        </Table>

        {totalPages && totalPages > 1 && (
          <Fragment>
            <Pagination
              defaultActivePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  page: state.schedules.page,
  totalPages: state.schedules.totalPages,
  dataIndex: state.schedules.dataIndex,
  dataMap: scheduleSelector.selectSchedulesDataMap(state),
  isFetching: state.schedules.isFetching,
  isErrored: state.api.isError,
  listScheduleAvailability: selectOperationAvailability(state)(
    AT_TYPES.SCHEDULE,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getSchedules: searchParams => dispatch(getSchedules(searchParams)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(SCHEDULES, entity, operationName, data, options))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withConfirmationModal(Schedules));
